import React, {useEffect, useMemo} from 'react';
import {LeftsideNav} from '@components/layout/leftNav/LeftsideNav';
import {Roles} from '@hooks/user/useUserPermissions';
import Head from 'next/head';
import {useUniversalModalDispatch} from '@components/context/UniversalModalContext';
import {WhitePanel} from '@components/common/panels/WhitePanel';
import {
    SubscriptionPlan,
    useAccountDetailsQuery,
    useCreateStripeDeepLinkMutation,
    useCreateSubscriptionMutation,
    useStripeUpcomingInvoiceQuery,
    useSubscriptionPlansQuery
} from "@graphql/generated-graphql";
import {getClient} from "@graphql/graphql-client/graphClient";
import {Button, Toggle} from "@components/base";
import {useRouter} from "next/router";
import dayjs from "dayjs";
import {SimpleModal} from "@components/base/overlays/Modal/SimpleModal";
import SkipperOctopus from "public/svgs/skipper-octopus.svg";
import NextImage from "next/image";

export default function BillingPlans() {
    const router = useRouter();
    const {dispatchNotification} = useUniversalModalDispatch();
    const {toggle: toggleModal} = useUniversalModalDispatch();
    const {data, refetch} = useAccountDetailsQuery(getClient('users'));
    const {data: plansData} = useSubscriptionPlansQuery(getClient('users'));
    const [annualSwitch, setAnnualSwitch] = React.useState(true);
    const {data: stripeUpcomingInvoiceData, refetch: refetchInvoice} = useStripeUpcomingInvoiceQuery(getClient('users'));
    const startSubscription = useCreateSubscriptionMutation(getClient('users'), {
        onSuccess: (data, args) => {
            if (data?.createSubscription?.success) {
                refetch();
                refetchInvoice();
                if (data.createSubscription.redirectUrl)
                    router.push(data.createSubscription.redirectUrl, undefined, {shallow: false})
                else if (data.createSubscription.invoicePreview) {
                    toggleModal(
                        true,
                        <SimpleModal
                            body={<>
                                <div className={"mt-8"}>
                                    <NextImage
                                        src={SkipperOctopus}
                                        width={84}
                                        height={84}
                                        layout="fixed"
                                        className="pointer-events-none"
                                    />
                                </div>
                                <div className={"text-xl font-medium my-4"}>Update your subscription</div>
                                <div className={"px-8"}>
                                    <div className={""}>
                                        You are about to switch to the {data.createSubscription.subscriptionPlan.title} plan!
                                    </div>
                                    <div className={"mt-2 font-medium"}>
                                        Due today: <span className={"font-medium"}>${data.createSubscription.invoicePreview.amount_due / 100}</span>
                                    </div>
                                    <div className={"text-xs max-w-sm mx-auto mt-4 text-gray-700"}>
                                        By clicking &quot;Confirm Update&quot;, you agree to the new rate and our Terms of Service.
                                        This change will take effect immediately.
                                    </div>
                                </div>
                            </>}
                            confirmLabel={'Confirm Update'}
                            onConfirm={() => {
                                startSubscription.mutate({
                                    ...args,
                                    forceUpdate: true
                                })
                            }}
                        />,
                    );
                } else if (args.forceUpdate) {
                    dispatchNotification({
                        title: 'Subscription updated successfully',
                        text: '',
                        type: 'success',
                    });
                    [1, 3, 5, 10].map(i => setTimeout(refetch, i * 1000));
                    [1, 3, 5, 10].map(i => setTimeout(refetchInvoice, i * 1000));
                }
            }
        }
    })
    const handleStartSubscription = async (plan: SubscriptionPlan, priceId: string) => {
        if (!plan) return;
        if ((plan.maxCourses && plan.maxCourses < data?.account?.subscriptionTotalCourses ||
            plan.maxActivities && plan.maxActivities < data?.account?.subscriptionTotalActivities ||
            plan.maxStudents && plan.maxStudents < data?.account?.subscriptionTotalStudents ||
            plan.maxInstructors && plan.maxInstructors < data?.account?.subscriptionTotalInstructors)) {
            dispatchNotification({
                title: 'Please adjust your account usage to fit within the new plan limits',
                text: '',
                type: 'danger',
            });
            return;
        }
        // if (plan.priceMonthly < (data?.account?.subscriptionPlan?.priceMonthly)) {
        //     toggleModal(
        //         true,
        //         <SimpleModal
        //             type="warning"
        //             title={`Downgrade to ${plan.title}`}
        //             body={<>
        //                 Are you sure you want to downgrade now?
        //                 <div className={"mt-4 px-16 text-sm text-orange-500"}>
        //                     NOTE - there are no refunds, any unused time will be prorated and credited to the new or future plans
        //                 </div>
        //             </>}
        //             confirmLabel={'Downgrade'}
        //             onConfirm={() => {
        //                 startSubscription.mutate({
        //                     priceId,
        //                     subscriptionPlanId: plan.id,
        //                 })
        //             }}
        //         />,
        //     );
        // } else
        startSubscription.mutate({
            priceId,
            subscriptionPlanId: plan.id,
        })
    }

    const createStripeDeepLink = useCreateStripeDeepLinkMutation(getClient('users'), {
        onSuccess: (data) => {
            if (data?.createStripeDeepLink?.url) {
                router.push(data.createStripeDeepLink.url);
            }
        }
    });

    useEffect(() => {
        let {success, error, emailConfirmed} = router.query as Record<string, string>;
        if (success) {
            dispatchNotification({
                title: success,
                text: '',
                type: 'success',
            });
            router.replace(router.pathname, undefined, {shallow: true});
        } else if (error) {
            dispatchNotification({
                title: error,
                text: '',
                type: 'danger',
            });
            router.replace(router.pathname, undefined, {shallow: true});
        }
    }, [router.query]);

    const isDefaultFreePlan = data?.account?.subscriptionPlan?.productId == 'custom' && data?.account?.subscriptionCustomPrice == 0;
    const basicPlan = useMemo(() => plansData?.subscriptionPlans?.find(p => p.productId == 'basic'), [plansData]);

    return (
        <LeftsideNav
            bgColor="bg-gray-50"
            breadCrumb="Billing and Plans"
            breadCrumbUrl="/account/billing">
            <Head>
                <title>{'Billing and Plans | Skipper'}</title>
            </Head>
            <div className="flex flex-col w-full pt-4 pb-20">
                <WhitePanel>
                    <div>
                        <h1 className="text-xl mb-1">Billing and Plans</h1>
                    </div>
                    {/*<div>*/}
                    {/*    <p className="text-sm">*/}
                    {/*        Manage your Skipper account subscription*/}
                    {/*    </p>*/}
                    {/*</div>*/}
                    <div className={"my-6"}>
                        <div className="w-full min-w-full font-medium text-base my-1">
                            Your Current Plan:
                            <span className={"mx-1 font-medium"}>
                                {data?.account?.subscriptionPlan?.title || 'none'}
                            </span>
                        </div>
                        {data?.account?.subscriptionEndDate
                            ? <div className="w-full min-w-full text-sm">
                                Next payment due on
                                {stripeUpcomingInvoiceData?.stripeUpcomingInvoice && <>
                                    <span className={"mx-1"}>
                                        {dayjs(stripeUpcomingInvoiceData.stripeUpcomingInvoice.created * 1000).format('MMMM DD, YYYY')}
                                    </span>
                                    <span className={"font-bold"}>${((stripeUpcomingInvoiceData.stripeUpcomingInvoice.amount_due || 0) / 100).toFixed(2)}</span>
                                </>}
                            </div>
                            : <div className="w-full min-w-full text-sm">
                                No payment due:
                                <span className={"mx-1 font-bold"}>$0.00</span>
                            </div>}
                        {data?.account?.subscriptionPlan?.productId != 'custom' && <>
                            <div className={"flex gap-4 mt-2"}>
                                {(data?.account?.subscriptionCustomPrice || 0) != 0 &&
                                    <a href={"#"}
                                       className="text-sm text-teal-600 hover:underline hover:text-teal-500"
                                       onClick={() => createStripeDeepLink.mutate({type: 'manage-payment'})}>
                                        Manage Payment Method
                                    </a>}
                                <a href={"#"}
                                   className="text-sm text-teal-600 hover:underline hover:text-teal-500"
                                   onClick={() => createStripeDeepLink.mutate({type: 'payment-history'})}>
                                    View Payment History
                                </a>
                            </div>
                        </>}
                    </div>

                    <div className={"my-6"}>
                        <div className="w-full min-w-full font-medium text-base my-1">
                            Current Usage
                        </div>
                        <ul className={"text-sm list-disc ml-4"}>
                            <li>
                                {data?.account?.subscriptionTotalCourses} active courses
                            </li>
                            <li>
                                {data?.account?.subscriptionTotalActivities} activities
                            </li>
                            <li>
                                {data?.account?.subscriptionTotalStudents} students
                            </li>
                            <li>
                                {data?.account?.subscriptionTotalInstructors} course leaders
                            </li>
                        </ul>
                    </div>

                    <div className="flex gap-2 text-sm">
                        <div>Monthly Plans</div>
                        <Toggle
                            enabled={annualSwitch}
                            type="button"
                            bidirectional={true}
                            setEnabled={() => setAnnualSwitch(!annualSwitch)}
                        />
                        <div>Annual Plans</div>
                    </div>

                    <div className="w-full my-4 flex justify-center gap-4 flex-wrap">
                        {plansData?.subscriptionPlans?.filter(p => p.productId != 'custom' && p.productId != 'business').map(plan => {
                            const isCurrentLevel = data?.account?.subscriptionPlan?.productId === plan.productId;
                            const currentIsMonthly = data?.account?.subscriptionCustomInterval?.toLowerCase() === 'month';
                            return <div key={plan.id}
                                        className={`text-sm min-w-40 text-center border border-gray-150 shadow shadow-xs rounded-lg p-3 ${data?.account?.subscriptionPlan?.productId === plan.productId ? 'bg-teal-050' : ''}`}>
                                <div className={"text-base font-medium"}>
                                    {plan.title}
                                </div>

                                <div className={"my-3 flex flex-col gap-2 h-12"}>
                                    <div className={"text-xl font-medium"}>
                                        ${!annualSwitch ? plan.priceMonthly : (plan.priceAnnual / 12)}
                                        <span className={"text-base"}>/mo</span>
                                    </div>
                                    {plan.priceMonthly > 0 && <div className={"text-xs"}>
                                        {!annualSwitch ? 'Billed monthly' : 'Billed annually'}
                                    </div>}
                                </div>

                                <Button
                                    variant={isCurrentLevel ? "subtle" : plan.priceMonthly >= (data?.account?.subscriptionPlan?.priceMonthly) ? "primary" : "white"}
                                    size={"small"}
                                    className="w-32 px-0 my-4 justify-center"
                                    // disabled={startSubscription.isLoading}
                                    onClick={() => (!isCurrentLevel || annualSwitch && currentIsMonthly || !annualSwitch && !currentIsMonthly) && handleStartSubscription(plan, (annualSwitch ? plan.annualPriceId : plan.monthlyPriceId))}>
                                    {isCurrentLevel
                                        ? (annualSwitch && currentIsMonthly ? 'Go Yearly' : !annualSwitch && !currentIsMonthly ? 'Go Monthly' : 'Current Plan')
                                        : isDefaultFreePlan ? 'Select' :
                                            plan.priceMonthly >= (data?.account?.subscriptionPlan?.priceMonthly) ? 'Upgrade' : 'Downgrade'}
                                </Button>

                                <ul className={"text-xs list-disc mx-4 text-left leading-5 text-gray-700"}>
                                    {plan.features.map((feature, index) => (
                                        <li key={index}>{feature}</li>
                                    ))}
                                </ul>
                            </div>
                        })}
                    </div>

                    <div className="w-full border border-gray-150 shadow shadow-xs rounded-lg flex p-4 my-8">
                        <div className={"w-48 flex flex-col gap-1"}>
                            <div className={"text-base font-medium"}>
                                Business
                            </div>
                            <div className={"my-3 flex flex-col gap-2"}>
                                <div className={"text-xl font-medium"}>
                                    Custom pricing
                                </div>
                            </div>
                            <div>
                                <Button
                                    variant="primary"
                                    className="px-2"
                                    size={"small"}
                                    onClick={() => router.push('mailto:sales@skipper.io')}
                                    type="submit">
                                    Talk to Sales
                                </Button>
                            </div>
                        </div>
                        <div className={"flex-1 flex flex-col gap-4 justify-center"}>
                            <div className={"text-sm"}>
                                Are you an organization, non-profit, co-op, or school? Contact us for special pricing.
                            </div>
                            <div className={"text-sm"}>
                                Including white-glove onboarding, private training, priority customer support, and special pricing for academic add-on’s (e.g., Turnitin, Career Discovery Platform)
                            </div>
                        </div>
                    </div>

                    {data?.account?.subscriptionPlan &&
                        data.account.subscriptionPlan.productId !== 'basic' && data
                            .account.subscriptionPlan.productId !== 'custom' && <>
                            <hr className="my-8"/>

                            <p className={"text-lg font-medium"}>Cancel Subscription</p>
                            <p className={"text-xs leading-5 mt-1"}>
                                Upon cancelling your subscription, you will be downgraded to our Free plan.
                                You can always join another account&#39;s course as a course leader, parent observer, or
                                student, free of charge.
                            </p>
                            <div className={"my-4"}>
                                <p className={"text-xs leading-5"}>
                                    Please adjust your account if it currently exceeds these limits before proceeding with cancellation:
                                </p>
                                <ul className={"list-disc mx-4 text-xs text-left leading-5"}>
                                    <li>1 active course</li>
                                    <li>100 activities</li>
                                    <li>10 students</li>
                                    <li>2 instructors/course leaders</li>
                                </ul>
                            </div>

                            <Button
                                variant="red"
                                size={"small"}
                                className=""
                                onClick={() => handleStartSubscription(basicPlan, basicPlan?.annualPriceId)}>
                                Cancel Subscription
                            </Button>
                        </>}
                </WhitePanel>
            </div>
        </LeftsideNav>
    );
}

BillingPlans.displayName = 'Billing and Plans';
BillingPlans.useDefault = true;
BillingPlans.requiredRole = Roles.Adult;